import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { MaskedLine, Float, EASE } from "@/components/motion/Reveal";

const HERO_BG =
  "https://images.unsplash.com/photo-1599743777555-e362a2feab39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHxf||oman&q=85&w=1800";
const HERO_VISUAL =
  "https://images.pexels.com/photos/30547579/pexels-photo-30547579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720";

export default function Hero() {
  const { t } = useI18n();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 md:pt-44 md:pb-32 bg-white"
    >
      {/* Muscat skyline — ultra-subtle editorial backdrop with parallax */}
      <motion.div className="absolute inset-0" style={{ y: yBg }} aria-hidden>
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/70 to-white" />
      </motion.div>
      <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
      <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
      <div className="absolute -top-32 end-[-10%] h-[520px] w-[520px] rounded-full bg-brand-red/10 blur-3xl" aria-hidden />

      <div className="container-pro relative grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-7">
          <motion.div
            className="flex items-center gap-3 mb-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <span className="h-2 w-2 rounded-full bg-brand-red animate-pulse-dot" aria-hidden />
            <span className="overline">{h.kicker}</span>
          </motion.div>

          <h1 className="h1-display text-5xl sm:text-6xl md:text-7xl text-balance" data-testid="hero-headline">
            <MaskedLine delay={0.15}>{h.headline1}</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="text-brand-red">{h.headline2}</span>
            </MaskedLine>
          </h1>

          <motion.p
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-ink-secondary"
            data-testid="hero-subhead"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
          >
            {h.subhead}
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap items-center gap-3"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.62 }}
          >
            <motion.a
              href="#contact"
              className="btn-primary"
              data-testid="hero-cta"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {h.ctaPrimary}
              <ArrowRight size={16} data-mirror-rtl="true" />
            </motion.a>
            <motion.a
              href="#services"
              className="btn-ghost"
              data-testid="hero-secondary-cta"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {h.ctaSecondary}
              <ArrowUpRight size={16} data-mirror-rtl="true" />
            </motion.a>
          </motion.div>

          <motion.div
            className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-line pt-6"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.8 } } }}
          >
            <Stat label={h.stat1Label} value={h.stat1Value} />
            <Stat label={h.stat2Label} value={h.stat2Value} />
            <Stat label={h.stat3Label} value={h.stat3Value} />
          </motion.div>
        </div>

        {/* Visual — clipped frame, parallax drift, floating chips */}
        <motion.div className="md:col-span-5 relative" style={{ y: yVisual, opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 32 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.4 }}
            className="relative aspect-[4/5] overflow-hidden border border-line-dark bg-surface-dark shadow-[0_32px_80px_-24px_rgba(9,9,11,0.45)]"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 28px), calc(100% - 28px) 100%, 0 100%)" }}
          >
            <img src={HERO_VISUAL} alt="Abstract digital network" className="absolute inset-0 h-full w-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" aria-hidden />
            <div className="absolute inset-0 dot-bg opacity-40" aria-hidden />

            <div dir="ltr" className="absolute top-4 start-4 end-4 flex items-center justify-between text-[10px] font-mono tracking-widest text-white/80">
              <span>{h.imageCorner}</span>
              <span>{h.imageLocation}</span>
            </div>
            <div className="absolute bottom-4 start-4 end-4 flex items-end justify-between text-white">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{h.imageVisionLabel}</div>
                <div className="font-heading text-lg leading-tight mt-1">{h.imageVisionTitle}</div>
              </div>
              <div className="h-10 w-10 rounded-full border border-white/30 flex items-center justify-center">
                <div className="h-2 w-2 rounded-full bg-brand-red" />
              </div>
            </div>
          </motion.div>

          {/* corner accent */}
          <div className="absolute -top-2 -end-2 h-10 w-10 border-t-2 border-e-2 border-brand-red" aria-hidden />

          <Float className="absolute -start-3 -bottom-3 md:-start-6 md:-bottom-6 hidden sm:block" distance={8} duration={6}>
            <div className="bg-white/90 backdrop-blur-md border border-line rounded-lg px-4 py-3 shadow-lg">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{h.chipLabel}</div>
              <div className="font-heading text-sm font-medium mt-0.5">{h.chipValue}</div>
            </div>
          </Float>
        </motion.div>
      </div>
    </section>
  );
}

const statVar = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const Stat = ({ label, value }) => (
  <motion.div variants={statVar}>
    <div className="font-mono text-xs text-ink-muted tracking-widest uppercase">{label}</div>
    <div className="font-heading text-xl font-semibold mt-1 text-ink-primary">{value}</div>
  </motion.div>
);
